var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useMergedClasses } from '../styles/classes';
import { styled } from '../styled';
import { Text } from '../text';
import clsx from '../utils/clsx';
import { getLinkUtilityClass, linkClasses, linkComponentName } from './link-classes';
var Root = styled(Text, { name: linkComponentName, slot: 'Root' })(function (_a) {
    var _b;
    var theme = _a.theme, ownerState = _a.ownerState;
    var colorStyleMap = {
        danger: {
            color: theme.tokens.color_fg_link_danger,
            focusStyles: theme.mixins.focusRing({ color: theme.tokens.color_border_focus_state_danger }),
            hoverStyles: {
                color: theme.tokens.color_fg_link_danger_hover,
            },
            activeStyles: {
                color: theme.tokens.color_fg_link_danger_pressed,
            },
        },
        primary: {
            color: theme.tokens.color_fg_link_primary,
            hoverStyles: {
                color: theme.tokens.color_fg_link_primary_hover,
            },
            activeStyles: {
                color: theme.tokens.color_fg_link_primary_pressed,
            },
        },
        textPrimary: {
            color: theme.tokens.color_fg_link,
            hoverStyles: {
                color: theme.tokens.color_fg_link_hover,
            },
            activeStyles: {
                color: theme.tokens.color_fg_link_pressed,
            },
        },
        textSecondary: {
            color: theme.tokens.color_fg_link_subtle,
            hoverStyles: {
                color: theme.tokens.color_fg_link_subtle_hover,
            },
            activeStyles: {
                color: theme.tokens.color_fg_link_subtle_pressed,
            },
        },
        inherit: {
            color: 'inherit',
        },
    };
    var colorStyles = colorStyleMap[ownerState.color];
    return __assign(__assign({ display: 'inline', alignItems: 'center', textDecoration: 'none', borderRadius: 2, outline: 'none', color: colorStyles.color, '&:focus-visible': colorStyles.focusStyles || theme.mixins.focusRing(), '&:hover': __assign({ textDecoration: ownerState.underline === 'hover' ? 'underline' : 'none' }, colorStyles.hoverStyles), '&:active': __assign({}, colorStyles.activeStyles) }, (ownerState.component === 'button' && {
        display: 'inline-flex',
        verticalAlign: 'initial',
        position: 'relative',
        // pulled from MUI: https://github.com/mui/material-ui/blob/master/packages/mui-material/src/Link/Link.js#L63-L85
        WebkitTapHighlightColor: 'transparent',
        backgroundColor: 'transparent',
        // We disable the focus ring for mouse, touch and keyboard users.
        outline: 0,
        border: 0,
        margin: 0,
        borderRadius: 0,
        padding: 0,
        cursor: 'pointer',
        userSelect: 'none',
        MozAppearance: 'none',
        WebkitAppearance: 'none',
        '&::-moz-focus-inner': {
            borderStyle: 'none', // Remove Firefox dotted outline.
        },
    })), (_b = {}, _b["& > .".concat(linkClasses.icon)] = {
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
    }, _b['& > svg'] = {
        width: '1em',
        height: '1em',
        '&:first-child': {
            marginInlineEnd: theme.spacing(1),
        },
    }, _b));
});
var StartIcon = styled('span', {
    name: linkComponentName,
    slot: 'StartIcon',
})(function (_a) {
    var theme = _a.theme;
    return ({
        verticalAlign: 'text-top',
        marginInlineEnd: theme.spacing(0.5),
    });
});
var EndIcon = styled('span', { name: linkComponentName, slot: 'EndIcon' })(function (_a) {
    var theme = _a.theme;
    return ({
        verticalAlign: 'text-top',
        marginInlineStart: theme.spacing(0.5),
    });
});
export var Link = React.forwardRef(function (props, ref) {
    var className = props.className, children = props.children, startIcon = props.startIcon, endIcon = props.endIcon, _a = props.color, color = _a === void 0 ? 'primary' : _a, _b = props.component, component = _b === void 0 ? 'a' : _b, _c = props.underline, underline = _c === void 0 ? 'none' : _c, _d = props.variant, variant = _d === void 0 ? 'inherit' : _d, 
    // remove to avoid forwarding
    propClasses = props.classes, rootProps = __rest(props, ["className", "children", "startIcon", "endIcon", "color", "component", "underline", "variant", "classes"]);
    var classes = useMergedClasses(linkClasses, getLinkUtilityClass, propClasses);
    var ownerState = {
        color: color,
        component: component,
        underline: underline,
    };
    return (React.createElement(Root, __assign({ color: color === 'inherit' ? color : undefined, component: component, className: clsx([classes.root, className]), ref: ref, ownerState: ownerState, variant: variant }, rootProps),
        !!startIcon && (React.createElement(StartIcon, { className: clsx(classes.icon, classes.startIcon, classes.startIcon) }, startIcon)),
        children,
        !!endIcon && React.createElement(EndIcon, { className: clsx(classes.icon, classes.endIcon) }, endIcon)));
});
